import React, { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          'timeout-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Public site key; fallback because the hosted env can reset VITE_ values.
  const siteKey =
    (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
    '0x4AAAAAAFMsdl3LXJNgkFZ1';

  useEffect(() => {
    if (!siteKey) return;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current) return;
      if (widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError('');
          },
          'error-callback': () => {
            setTurnstileToken('');
            setTurnstileError(
              'Verification could not be completed. Please try again.'
            );
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          'timeout-callback': () => {
            setTurnstileToken('');
          },
        }
      );
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src^="${TURNSTILE_SCRIPT_SRC}"]`
    );
    if (!script) {
      script = document.createElement('script');
      script.src = `${TURNSTILE_SCRIPT_SRC}?render=explicit`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderWidget);

    return () => {
      script?.removeEventListener('load', renderWidget);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        // ignore
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!siteKey) {
      setSubmitError(
        'The contact form is temporarily unavailable. Please try again later.'
      );
      return;
    }

    if (!turnstileToken) {
      setTurnstileError('Please complete the verification below.');
      return;
    }

    setIsSubmitting(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-contact-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            website: honeypot,
            turnstileToken,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to send message');
      }

      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setHoneypot('');
      resetTurnstile();

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to send message. Please try again later.';
      setSubmitError(message);
      resetTurnstile();
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !isSubmitting && !!turnstileToken;

  return (
    <div className="bg-black p-8 rounded-lg shadow-lg max-w-2xl mx-auto">
      <h2 className="section-title text-center mb-8">Get in Touch</h2>

      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-800 text-white rounded-md">
          Thank you for your message. We'll get back to you soon!
        </div>
      )}

      {submitError && (
        <div className="mb-6 p-4 bg-red-800 text-white rounded-md">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-gold mb-2">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="input-field"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="email" className="block text-gold mb-2">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="phone" className="block text-gold mb-2">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="input-field"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="message" className="block text-gold mb-2">
            Quote or Questions
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            className="input-field resize-none"
          ></textarea>
        </div>

        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="mb-6 flex flex-col items-center">
          <div
            ref={turnstileContainerRef}
            className="flex justify-center w-full overflow-x-auto"
          />
          {turnstileError && (
            <p className="mt-3 text-sm text-red-400 text-center">
              {turnstileError}
            </p>
          )}
          {!siteKey && (
            <p className="mt-3 text-sm text-red-400 text-center">
              Verification is not configured yet.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary w-full flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
